import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { formatDateTime } from '@src/containers/App/utils/Formatter'
import {
  MerchantReportSummaryDto,
  useForceGenerateReport,
  useGenerateAllTimeReport,
  useGetAllTimeReportStatus,
  useInvalidateMerchantReports,
} from '@src/data/api/merchants-api/merchants-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AsyncActionButton from './AsyncActionButton'
import DownloadButton from './DownloadButton'

const TEN_MINUTES_MS = 10 * 60 * 1000

type UseAllTimeReportResult = [
  generateAllTimeReport: () => Promise<void>,
  isGeneratingAllTime: boolean,
  isFetchingAllTime: boolean,
  isGenerating: boolean,
  showRecentWarning: boolean,
  highlightedFile: string | null,
  acknowledgeReport: () => void,
]

function useAllTimeReport(merchantId: string): UseAllTimeReportResult {
  const [generateAllTimeReportMutation, isGeneratingAllTime, resetAllTime] = useGenerateAllTimeReport(merchantId)
  const [allTimeReport, isFetchingAllTime] = useGetAllTimeReportStatus(merchantId)
  const invalidateMerchantReports = useInvalidateMerchantReports(merchantId)

  const [showRecentWarning, setShowRecentWarning] = useState(false)
  const [hasNewReport, setHasNewReport] = useState(false)
  const prevStatusRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const currentStatus = allTimeReport?.status
    if (prevStatusRef.current === 'generating' && currentStatus === 'ready') {
      setHasNewReport(true)
      invalidateMerchantReports()
    }
    prevStatusRef.current = currentStatus
  }, [allTimeReport?.status, invalidateMerchantReports])

  const generateAllTimeReport = useCallback(async () => {
    if (allTimeReport?.status === 'generating') return

    if (allTimeReport?.status === 'ready' && allTimeReport.generatedOn) {
      const elapsed = Date.now() - new Date(allTimeReport.generatedOn).getTime()
      if (elapsed < TEN_MINUTES_MS) {
        setShowRecentWarning(true)
        return
      }
    }

    setShowRecentWarning(false)
    await generateAllTimeReportMutation()
    resetAllTime()
  }, [allTimeReport, generateAllTimeReportMutation, resetAllTime])

  const isGenerating = allTimeReport?.status === 'generating'
  const highlightedFile = hasNewReport ? allTimeReport?.fileName ?? null : null

  const acknowledgeReport = useCallback(() => {
    setHasNewReport(false)
  }, [])

  return [
    generateAllTimeReport,
    isGeneratingAllTime,
    isFetchingAllTime,
    isGenerating,
    showRecentWarning,
    highlightedFile,
    acknowledgeReport,
  ]
}


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

interface Props {
  merchantId: string
  reportsSummary: MerchantReportSummaryDto[]
  isFetchingReports: boolean
  generateReportDisabled: boolean
  onGenerateReport: () => void
}

const ReportsList = ({
  merchantId,
  reportsSummary,
  isFetchingReports,
  generateReportDisabled,
  onGenerateReport,
}: Props) => {
  const { t } = useTranslation()
  const [forceGenerateReport, isPending, reset] = useForceGenerateReport(merchantId)
  const [
    generateAllTimeReport,
    isGeneratingAllTime,
    isFetchingAllTime,
    isGenerating,
    showRecentWarning,
    highlightedFile,
    acknowledgeReport,
  ] = useAllTimeReport(merchantId)

  const handleforceGenerateReport = async () => {
    await forceGenerateReport()
    reset()
    onGenerateReport()
  }

  return (
    <TableContainer component={Paper}>
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography variant="h6" gutterBottom component="div" sx={{ mt: 1, ml: 2, mr: 1, flexShrink: 0 }}>
          {t('common.reports')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
          <AsyncActionButton
            variant="outlined"
            size="large"
            isPending={isGeneratingAllTime || isFetchingAllTime || isGenerating}
            onClick={generateAllTimeReport}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('common.generateAllTimeReport')}
          </AsyncActionButton>
          {showRecentWarning && (
            <Typography variant="caption" color="error">
              {t('common.allTimeReportCooldown')}
            </Typography>
          )}
        </Box>
        <Tooltip
          title={generateReportDisabled ? t('common.generateDisabled') : t('common.generateSendReport')}
          arrow
          placement="top"
        >
          <span>
            <AsyncActionButton
              variant="outlined"
              size="large"
              isPending={isPending || isFetchingReports}
              disabled={generateReportDisabled}
              onClick={async () => {
                await handleforceGenerateReport()
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('common.generateSendReport')}
            </AsyncActionButton>
          </span>
        </Tooltip>
      </Toolbar>
      <Table sx={{ maxHeight: '25rem' }} aria-label="custom pagination table">
        <TableHead>
          <TableRow>
            <StyledTableCell>{t('common.fileName')}</StyledTableCell>
            <StyledTableCell sx={{ textAlign: 'center' }}>{t('common.createdOn')}</StyledTableCell>
            <StyledTableCell sx={{ textAlign: 'center' }}>{t('common.download')}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(isFetchingReports || isPending || isGenerating) && (
            <TableRow>
              <TableCell colSpan={20} style={{ padding: 0 }}>
                <LinearProgress style={{ height: '10px' }} />
              </TableCell>
            </TableRow>
          )}
          {(!reportsSummary || reportsSummary.length === 0) && !(isFetchingReports || isPending || isGenerating) && (
            <TableRow>
              <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                {t('common.noResultsFound')}
              </TableCell>
            </TableRow>
          )}
          {reportsSummary?.map((row) => {
            const isHighlighted = highlightedFile != null && row.fileName.includes(highlightedFile)
            return (
              <TableRow key={row.fileName}>
                <TableCell>{row.fileName.replace(`Merchants/${merchantId}/`, '')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{formatDateTime(row.createdOn)}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <span
                    className={isHighlighted ? 'download-highlight' : undefined}
                    onClick={isHighlighted ? acknowledgeReport : undefined}
                  >
                    <IconButton>
                      <DownloadButton fileName={row.fileName} sasTokenUrl={row.sasTokenUrl} />
                    </IconButton>
                  </span>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
export default ReportsList
